<template>
  <div class="pk-tooltip">
    <slot name="activator" />

    <transition name="fade">
      <div
        v-show="value || isVisible"
        ref="tooltip"
        class="pk-tooltip__tooltip"
        :style="tooltipStyle"
      >
        <slot />
      </div>
    </transition>
  </div>
</template>

<style>
.pk-tooltip__tooltip {
  z-index: 999999;
}
/* 
.fade-enter-active {
  transition: all 125ms ease;
}
.fade-leave-active {
  transition: all 125ms cubic-bezier(1, 0.5, 0.8, 1);
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
*/
</style>

<script>
import { createPopper, Instance } from '@popperjs/core';

/**
 * Popper tooltip
 * 
 * Based in Prozis Kit Popper tooltip by Hugo Rodrigues
 * 
 *
 * @example
 * ```html
 * <Popper position="bottom">
 *   <template #activator>
 *     <span>{{ title }}</span>
 *   </template>
 *   {{content}}
 * </Popper>
 * ```
 */
export default {
  name: 'Popper',
  components: {
  },
  props: {
    fallbackPlacements: null,
    // used for inputs?
    value: { default: false, type: Boolean },
    strategy: {
      // 'fixed' | 'absolute'
      default: 'fixed',
      type: String
    },
    position: {
      // 'auto'
      // | 'auto-start'
      // | 'auto-end'
      // | 'top'
      // | 'top-start'
      // | 'top-end'
      // | 'bottom'
      // | 'bottom-start'
      // | 'bottom-end'
      // | 'left'
      // | 'left-start'
      // | 'left-end'
      // | 'right'
      // | 'right-start'
      // | 'right-end';
      default: 'auto',
      type: String
    },
    trigger: {
      // 'hover' | 'clickToToggle'
      default: 'hover',
      type: String
    },
    closeOnClickOutside: { default: true, type: Boolean },
    hideOnScroll: { default: true, type: Boolean },
  },
  data: function() {  
    return {
        // Visibility state
      isVisible: false,

      // Popper instance
      poperInstance: null,

      // Html element activate tooltip
      activator: null,

      // Tooltip element
      tooltip: null,

      // Tooltip inline style
      tooltipStyle: null,
    }
  },
  methods:{
    /**
     * Show tooltip
     */
    show() {
      this.isVisible = true;

      if (this.poperInstance) {
        this.poperInstance.update();
      }

      this.$emit('input', true);
    },

    /**
     * Hide tooltip
     */
    hide() {
      this.isVisible = false;
      this.$emit('input', false);
    },

    /**
     * Toggle visibility
     */
    toggle() {
      if (this.isVisible) {
        this.hide();
      } else {
        this.show();
      }
    },

    handleDocumentClick(event){
        if (!this.activator || !this.tooltip || !this.poperInstance ||
          this.elementContains(this.tooltip, event.target) ||
          this.elementContains(this.activator, event.target)) {
            //console.log('handleDocumentClick ignore')
            return;
        }
        if(this.elementContains(this.$el, event.target)){
          this.toggle();
        }
        else{
          this.hide();
        }
    },

    handleDocumentScroll(event){
        if (!this.activator || !this.tooltip || !this.poperInstance) {
            return;
        }
        if(this.isVisible){
          this.hide();
        }
    },

    elementContains(elm, otherElm) {
      if (typeof elm.contains === 'function') {
        return elm.contains(otherElm);
      }
      return false;
    }, 

    listeners(){
      if(this.trigger === 'hover'){
        return {
          mouseenter: this.show,
          focus: this.show,
          mouseleave: this.hide,
          blur: this.hide,
        } 
      }
      else if(this.trigger === 'clickToToggle'){
        return {
          click: this.toggle,
        } 
      }
      return {};
    }
  },



  /**
   * Lifecycle
   */
  destroyed() {
    if (this.activator) {
        const listeners = this.listeners();
        for(let e in listeners){
          this.activator.removeEventListener(e, listeners[e]);
        }
    }  

    if(this.closeOnClickOutside){
      // remove click outside
      document.removeEventListener('click', this.handleDocumentClick);
    }

    if(this.hideOnScroll){
      // remove click outside
      window.removeEventListener('scroll', this.handleDocumentScroll);
    }

    if (this.poperInstance) {
      this.poperInstance.destroy();
    }
  },

  /**
   * Lifecycle
   */
  mounted() {
    this.tooltipStyle = {
      backgroundColor: this.bgColor,
      color: this.textColor
    };

    this.$nextTick(() => {
      this.activator = this.$el.children.item(0);
      this.tooltip = this.$refs.tooltip;

      if (!this.activator || !this.tooltip) {
        //console.log('popper: no activator or no tooltip', this.$el)
        return;
      }

      this.poperInstance = createPopper(this.activator, this.tooltip, {
        modifiers: [
          {
            name: 'preventOverflow',
            options: {
              altAxis: true,
              mainAxis: true
            }
          },
          {
            name: 'offset',
            options: {
              offset: [0, 4]
            }
          },
          {
            name: 'flip',
            options: {
              fallbackPlacements: this.fallbackPlacements ? this.fallbackPlacements : ['top', 'bottom', 'left', 'right']
            }
          }
        ],
        placement: this.position,
        strategy: this.strategy
      });

      if (this.activator) {
        const listeners = this.listeners();
        for(const e in listeners){
          this.activator.addEventListener(e, listeners[e]);
        }
      }

      if(this.closeOnClickOutside){
        // activate click outside
        document.addEventListener('click', this.handleDocumentClick);
      }

      if(this.hideOnScroll){
        // activate scroll listener
        window.addEventListener('scroll', this.handleDocumentScroll);
      }

    });
  },
}
</script>
<style lang="scss">
    .pk-tooltip__tooltip {
        z-index: 999999;
    }
</style>
